import { useCallback, useMemo } from 'react';
import noop from 'lodash/noop';

import { t, Trans } from '@lingui/macro';

import ChevronRight from '@decisiv/iconix/lib/components/ChevronRight';

import { H4, P } from '@decisiv/ui-components/lib/components/Typography';
import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Select from '@decisiv/ui-components/lib/components/Select';

import SentinelTeamForm from 'components/SentinelTeamForm';
import TeamKeyForm from 'components/TeamKeyForm';
import { useWizardModal } from 'components/WizardModal';

import { useCurrentUserContext } from 'utils/User';
import { useOemSegmentsFiltersQuery } from 'pages/Management/OemSegments/hooks/useOemSegmentsFiltersQuery';

import { useManagementTeamModalContext } from '../contexts/ManagementTeamModalContext';

export default function ManagementTeamProfileStep() {
  const { handleNextPage } = useWizardModal();
  const {
    values,
    setValues,
    canGoNext,
    logoUrl,
    onSaveImage,
    requiresOemSegmentSelector,
  } = useManagementTeamModalContext();

  const { isSuperUser, oemSegments: rawOemSegments } = useCurrentUserContext();
  /* istanbul ignore next -- @preserve */
  const userOemSegments = useMemo(() => rawOemSegments ?? [], [rawOemSegments]);

  const { teamKey, applicationName, teamName, oemSegmentId } = values;

  // Fetch OEM segments for Super Admin selector with pagination support
  const {
    data: oemSegmentsData,
    loading: oemSegmentsLoading,
    loadMore: loadMoreOemSegments,
  } = useOemSegmentsFiltersQuery({
    variables: { page: 1, text: '' },
    skip: !isSuperUser,
  });

  const oemSegmentOptions = useMemo(() => {
    // Super Admin: use paginated query results
    if (isSuperUser) {
      const collection = oemSegmentsData?.managedOemSegments?.collection ?? [];

      return collection.map((seg) => ({
        id: seg.id,
        label: seg.name,
        value: seg.id,
      }));
    }

    // OEM Manager with multiple segments: use segments from user profile
    return userOemSegments.map((seg) => ({
      id: seg.id,
      label: seg.name,
      value: seg.id,
    }));
  }, [isSuperUser, oemSegmentsData, userOemSegments]);

  const handleTeamNameChange = useCallback(
    ({ currentTarget: { value } }) => {
      setValues((oldState) => ({ ...oldState, teamName: value }));
    },
    [setValues],
  );

  const handleApplicationNameChange = useCallback(
    ({ currentTarget: { value } }) => {
      setValues((oldState) => ({ ...oldState, applicationName: value }));
    },
    [setValues],
  );

  const handleTeamKeyChange = useCallback(
    (value: string) => {
      setValues((oldState) => ({ ...oldState, teamKey: value }));
    },
    [setValues],
  );

  /* istanbul ignore next -- @preserve */
  const handleOemSegmentChange = useCallback(
    (value: string) => {
      setValues((oldState) => ({ ...oldState, oemSegmentId: value }));
    },
    [setValues],
  );

  return (
    <Flex flexDirection="column" marginTop={1}>
      {requiresOemSegmentSelector && (
        <Flex flexDirection="column" marginBottom={1}>
          <H4 marginTop={1}>
            <Trans>OEM Segment</Trans>
          </H4>
          <P color="alaskanHusky" size="small" marginBottom={1}>
            <Trans>Select the OEM Segment this team belongs to.</Trans>
          </P>
          <Select
            required
            label={t`OEM Segment`}
            name="oemSegment"
            value={oemSegmentId ?? ''}
            // @ts-expect-error - KDS onChange signature
            onChange={handleOemSegmentChange}
            options={oemSegmentOptions}
            disabled={oemSegmentsLoading}
            hideClearButton={false}
            onLoadMore={loadMoreOemSegments}
          />
        </Flex>
      )}

      <H4 marginTop={1}>
        <Trans>Team Profile</Trans>
      </H4>
      <P color="alaskanHusky" size="small" marginBottom={1}>
        <Trans>
          Configure the team&apos;s profile. Team Admins can change these
          settings later.
        </Trans>
      </P>
      <SentinelTeamForm
        name={teamName}
        handleNameChange={handleTeamNameChange}
        applicationName={applicationName}
        handleApplicationNameChange={handleApplicationNameChange}
        handleSaveImage={
          /* istanbul ignore next -- @preserve */ onSaveImage ?? noop
        }
        logoUrl={logoUrl}
        stretch
      />

      <H4 marginTop={2}>
        <Trans>Team Key</Trans>
      </H4>
      <P color="alaskanHusky" size="small" marginBottom={1}>
        <Trans>
          Unique identifier added as a prefix to all appointment numbers.
        </Trans>
      </P>
      <TeamKeyForm
        handleTeamKeyChange={handleTeamKeyChange}
        teamKey={teamKey}
        stretch
      />

      <Flex justifyContent="flex-end" marginTop={1.6}>
        <Button
          aria-label={t`Continue`}
          text={t`Continue`}
          variant="ghost"
          icon={ChevronRight}
          iconPosition="right"
          onClick={handleNextPage}
          disabled={!canGoNext(1)}
        />
      </Flex>
    </Flex>
  );
}
